import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAdminSocket } from '../hooks/useAdminSocket';
import { SessionHeader } from '../components/SessionHeader';
import { TeamConfig, type TeamColor, type CharacterSheet, type SheetAssignment } from '../components/TeamConfig';
import { CreateSheetModal } from '../components/CreateSheetModal';

type VotingMode = 'ALL_TOGETHER' | 'SEQUENTIAL';

interface GameSession {
  id: string;
  name: string;
  numPlayers: 2 | 3 | 4;
  status: 'DRAFT' | 'SETUP' | 'ACTIVE' | 'COMPLETED';
  votingMode: VotingMode;
  createdAt: string;
  startedAt?: string;
  endedAt?: string;
}

const TEAM_COLORS: Record<number, TeamColor[]> = {
  2: ['BLUE', 'RED'],
  3: ['BLUE', 'RED', 'GREEN'],
  4: ['BLUE', 'RED', 'GREEN', 'YELLOW'],
};

export function SessionSetup() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isConnected } = useAdminSocket(id);

  const [session, setSession] = useState<GameSession | null>(null);
  const [allSheets, setAllSheets] = useState<CharacterSheet[]>([]);
  const [assignments, setAssignments] = useState<Record<TeamColor, SheetAssignment[]>>({
    BLUE: [],
    RED: [],
    GREEN: [],
    YELLOW: [],
  });
  const [loading, setLoading] = useState(true);
  const [editingSheet, setEditingSheet] = useState<CharacterSheet | null>(null);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch session
      const sessionRes = await fetch(`/api/sessions/${id}`);
      const sessionData = await sessionRes.json();
      setSession(sessionData.session || sessionData);

      // Fetch all sheets
      const sheetsRes = await fetch('/api/sheets');
      const sheetsData = await sheetsRes.json();
      setAllSheets(sheetsData.sheets || sheetsData);

      // Fetch assignments for this session
      const assignmentsRes = await fetch(`/api/sheets/assignments?sessionId=${id}`);
      const assignmentsData = await assignmentsRes.json();

      // Group by team
      const grouped: Record<TeamColor, SheetAssignment[]> = {
        BLUE: [],
        RED: [],
        GREEN: [],
        YELLOW: [],
      };

      const assignmentsArray = Array.isArray(assignmentsData) ? assignmentsData : [];
      assignmentsArray.forEach((assignment: SheetAssignment) => {
        grouped[assignment.teamColor as TeamColor].push(assignment);
      });

      setAssignments(grouped);
    } catch (error) {
      console.error('[SessionSetup] Error fetching data:', error);
      alert('Failed to load session data');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignSheet = async (teamColor: TeamColor, sheetId: string, isPrimary: boolean): Promise<boolean> => {
    try {
      const response = await fetch('/api/sheets/assign', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: id,
          sheetId,
          teamColor,
          isPrimary,
        }),
      });

      if (response.ok) {
        await fetchData();
        return true;
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to assign sheet');
        return false;
      }
    } catch (error) {
      console.error('[SessionSetup] Error assigning sheet:', error);
      alert('Failed to assign sheet');
      return false;
    }
  };

  const handleUnassignSheet = async (assignmentId: string) => {
    if (!confirm('Remove this sheet assignment?')) return;

    try {
      const response = await fetch(`/api/sheets/assignment/${assignmentId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        await fetchData();
      } else {
        alert('Failed to remove assignment');
      }
    } catch (error) {
      console.error('[SessionSetup] Error removing assignment:', error);
      alert('Failed to remove assignment');
    }
  };

  const handleTogglePrimary = async (assignmentId: string, currentIsPrimary: boolean) => {
    try {
      const response = await fetch(`/api/sheets/assignment/${assignmentId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isPrimary: !currentIsPrimary }),
      });

      if (response.ok) {
        await fetchData();
      } else {
        alert('Failed to update assignment');
      }
    } catch (error) {
      console.error('[SessionSetup] Error updating assignment:', error);
      alert('Failed to update assignment');
    }
  };

  const handleGoLive = () => {
    navigate(`/admin/session/${id}/live`);
  };

  const handleBackToDashboard = () => {
    navigate('/admin');
  };

  const handleOpenDisplayWindow = () => {
    const displayUrl = `${window.location.origin}/display?session=${id}`;
    window.open(displayUrl, 'DungeonWorldDisplay', 'width=1920,height=1080');
  };

  const handleResetSession = async () => {
    if (!confirm('Sei sicuro di voler resettare la sessione?\n\nQuesto cancellerà:\n- Tutte le votazioni\n- Tutti gli spettatori connessi\n\nNON verranno cancellati:\n- Gli eroi e le loro schede\n- Le assegnazioni alle squadre')) {
      return;
    }

    try {
      const response = await fetch(`/api/sessions/${id}/reset`, {
        method: 'POST',
      });

      if (response.ok) {
        const data = await response.json();
        alert(`Reset completato!\n${data.votesDeleted} votazioni e ${data.audienceDeleted} spettatori rimossi.`);
        await fetchData(); // Refresh data
      } else {
        const error = await response.json();
        alert(error.error || 'Errore durante il reset');
      }
    } catch (error) {
      console.error('[SessionSetup] Error resetting session:', error);
      alert('Errore durante il reset della sessione');
    }
  };

  const handleVotingModeChange = async (mode: VotingMode) => {
    try {
      const response = await fetch(`/api/sessions/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ votingMode: mode }),
      });
      if (response.ok) {
        const data = await response.json();
        setSession(data.session);
      } else {
        alert('Failed to update voting mode');
      }
    } catch (error) {
      console.error('[SessionSetup] Error updating voting mode:', error);
      alert('Failed to update voting mode');
    }
  };

  const handleUpdateSheet = async (sheetData: Omit<CharacterSheet, 'id' | 'createdAt'>) => {
    if (!editingSheet) return;

    try {
      const response = await fetch(`/api/sheets/${editingSheet.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sheetData),
      });

      const data = await response.json();

      if (data.success) {
        await fetchData(); // Refresh data to get updated sheet
        setEditingSheet(null);
      } else {
        alert(data.error || 'Failed to update character sheet');
      }
    } catch (error) {
      console.error('[SessionSetup] Error updating sheet:', error);
      alert('Failed to update character sheet');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-12 bg-gray-800 rounded"></div>
            <div className="h-64 bg-gray-800 rounded"></div>
            <div className="h-64 bg-gray-800 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  const activeTeams = session ? TEAM_COLORS[session.numPlayers] : [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        <SessionHeader mode="setup" session={session} onNavigate={handleGoLive} onBack={handleBackToDashboard} />

        {/* Voting Mode Section */}
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 mb-8">
          <h2 className="text-xl font-bold text-white mb-2">Sistema Votazione Eroi</h2>
          <p className="text-gray-400 text-sm mb-4">
            Scegli come le squadre voteranno per i personaggi
          </p>
          <div className="flex gap-4">
            <button
              onClick={() => handleVotingModeChange('ALL_TOGETHER')}
              className={`px-6 py-3 rounded-lg font-semibold transition-colors ${
                session?.votingMode === 'ALL_TOGETHER'
                  ? 'bg-purple-600 text-white ring-2 ring-purple-400'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              Tutti insieme
            </button>
            <button
              onClick={() => handleVotingModeChange('SEQUENTIAL')}
              className={`px-6 py-3 rounded-lg font-semibold transition-colors ${
                session?.votingMode === 'SEQUENTIAL'
                  ? 'bg-purple-600 text-white ring-2 ring-purple-400'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              Singoli
            </button>
          </div>
          {session?.votingMode === 'ALL_TOGETHER' && (
            <p className="text-gray-500 text-sm mt-4">
              Tutte le squadre votano contemporaneamente.
            </p>
          )}
          {session?.votingMode === 'SEQUENTIAL' && (
            <p className="text-yellow-400 text-sm mt-4">
              Le squadre voteranno una alla volta. Potrai selezionare quale squadra vota dalla pagina Live.
            </p>
          )}
        </div>

        {/* Team Configuration Section */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-white mb-4">Configurazione Schede Squadre</h2>
          <p className="text-gray-400 text-sm mb-6">
            Assegna schede personaggio a ogni squadra. Le schede primarie verranno mostrate come opzioni di voto.
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {activeTeams.map((teamColor) => (
              <TeamConfig
                key={teamColor}
                teamColor={teamColor}
                allSheets={allSheets}
                assignments={assignments[teamColor]}
                onAssign={handleAssignSheet}
                onUnassign={handleUnassignSheet}
                onTogglePrimary={handleTogglePrimary}
                onEditSheet={setEditingSheet}
              />
            ))}
          </div>
        </div>

        {/* Display Test Section */}
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
          <h2 className="text-xl font-bold text-white mb-4">Display Window</h2>
          <p className="text-gray-400 text-sm mb-4">
            Open the display window on your projector or second screen to test projection modes
          </p>
          <div className="flex items-center gap-4">
            <button
              onClick={handleOpenDisplayWindow}
              className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              📺 Open Display Window
            </button>
            <div className="flex items-center gap-2">
              {isConnected ? (
                <>
                  <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                  <span className="text-green-400 text-sm font-semibold">Socket Connected</span>
                </>
              ) : (
                <>
                  <span className="w-3 h-3 bg-red-500 rounded-full"></span>
                  <span className="text-red-400 text-sm font-semibold">Socket Disconnected</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Reset Session Section */}
        <div className="bg-gray-800 rounded-xl p-6 border border-red-900/50">
          <h2 className="text-xl font-bold text-white mb-2">Reset Sessione</h2>
          <p className="text-gray-400 text-sm mb-4">
            Cancella tutte le votazioni e gli spettatori connessi. Gli eroi e le assegnazioni alle squadre verranno mantenuti.
          </p>
          <button
            onClick={handleResetSession}
            className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            🔄 Reset Sessione
          </button>
        </div>
      </div>

      {/* Edit Sheet Modal */}
      {editingSheet && (
        <CreateSheetModal
          sheet={editingSheet}
          onClose={() => setEditingSheet(null)}
          onSave={handleUpdateSheet}
        />
      )}
    </div>
  );
}
